/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { AuthenticationParameters, AuthResponse } from "msal";

import { msalApp, requiresInteraction, MAVEN_REQUESTS } from "../Authentication/AuthHelper";
import { HttpStatusCode } from "../Constants/HttpStatusCode";

export interface IHttpResponse<T> extends Response {
  parsedBody?: T;
  httpStatusCode?: number;
}

/**
 * Acquires authentication token through msal (OAUTH 2.0 flow)
 * @method
 * @param {AuthenticationParameters} request The request params.
 * @param {boolean} redirect Is redirect option enabled.
 * @returns {Promise<AuthResponse>} Authentication token response
 */
export async function acquireToken(
  request: AuthenticationParameters,
  redirect: boolean
): Promise<AuthResponse> {
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  return msalApp.acquireTokenSilent(request).catch((error) => {
    // Call interactive token request method in case of acquireTokenSilent failure
    // due to consent or interaction required
    if (requiresInteraction(error.errorCode)) {
      return redirect
        ? msalApp.acquireTokenRedirect({ ...request, redirectUri: window.location.origin })
        : msalApp.acquireTokenPopup(request);
    }
  });
}

/**
 * Utility function to send HTTP requests.
 * NOTE: Errors needs to be caught and handled by the caller as since  this is just a util, there is no telemetry instance.
 * @param {RequestInfo} request The Request info.
 * @param {boolean} isProtectedEndpoint If true get access token and attach it.
 * @param {boolean} isResponseJson If true the response is JSON.
 * @return {Promise<IHttpResponse<T>>} The Response.
 */
export async function http<T>(
  request: RequestInfo,
  isProtectedEndpoint = false,
  isResponseJson = true
): Promise<IHttpResponse<T>> {
  // In the case of the Service not being available, fetch will return a TypeError
  // Need to look into how fetch can handle a timeout, or if we implement our own
  // timeout on the frontend and send back a rejected promise.
  try {
    let response: IHttpResponse<T>;

    // if its protected, obtain token and call api.
    if (isProtectedEndpoint) {
      const tokenResponse = await acquireToken(MAVEN_REQUESTS.LOGIN, true);
      if (tokenResponse) {
        response = await fetch(request, {
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
            "Content-Type": "application/json",
          },
        });
      } else {
        // TODO: Log telemetry
        return Promise.reject("Unable to fetch authentication token.");
      }
    } else {
      response = await fetch(request);
    }

    // httpStatusCode.NoContent has no content/body
    // and fails response.json()
    if (response.status !== HttpStatusCode.NoContent && isResponseJson) {
      response.parsedBody = await response.json();
    }

    response.httpStatusCode = response.status;
    return response;
  } catch (error) {
    throw new Error(error);
  }
}

/**
 * Utility function to send GET HTTP requests.
 * @param {string} path The url info.
 * @param {boolean} isProtectedEndpoint Indicates if the endpoint is protected.
 * @param {RequestInit} args The arguments.
 * @return {Promise<IHttpResponse<T>>} The Response.
 */
export async function get<T>(
  path: string,
  isProtectedEndpoint = false,
  args: RequestInit = { method: "GET" }
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), isProtectedEndpoint);
}

/**
 * Utility function to send POST HTTP requests.
 * @param {string} path The url info.
 * @param {any} body The post body.
 * @param {boolean} isProtectedEndpoint Indicates if the endpoint is protected.
 * @param {boolean} isResponseJson If true the response is JSON.
 * @param {RequestInit} args The arguments.
 * @return {Promise<IHttpResponse<T>>} The Response.
 */
export async function post<T>(
  path: string,
  body: any,
  isProtectedEndpoint = false,
  isResponseJson = true,
  args: RequestInit = {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  }
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), isProtectedEndpoint, isResponseJson);
}

/**
 * Utility function to send PUT HTTP requests.
 * @param {string} path The url info.
 * @param {any} body The put body.
 * @param {boolean} isProtectedEndpoint Indicates if the endpoint is protected.
 * @param {RequestInit} args The arguments.
 * @return {Promise<IHttpResponse<T>>} The Response.
 */
export async function put<T>(
  path: string,
  body: any,
  isProtectedEndpoint = false,
  args: RequestInit = {
    method: "PUT",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  }
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), isProtectedEndpoint);
}

/**
 * Utility function to send DELETE HTTP requests.
 * @param {string} path The url info.
 * @param {any} body The delete body.
 * @param {boolean} isProtectedEndpoint Indicates if the endpoint is protected.
 * @param {RequestInit} args The arguments.
 * @return {Promise<IHttpResponse<T>>} The Response.
 */
export async function remove<T>(
  path: string,
  body: any,
  isProtectedEndpoint = false,
  args: RequestInit = {
    method: "DELETE",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  }
): Promise<IHttpResponse<T>> {
  return await http<T>(new Request(path, args), isProtectedEndpoint);
}

/**
 * Utility function to create auth token for requests.
 * @param {Headers} headers The headers info.
 * @param {any} body The delete body.
 */
export async function getAuthToken(headers: Headers): Promise<Headers> {
  const tokenResponse = await acquireToken(MAVEN_REQUESTS.LOGIN, true);
  if (tokenResponse) {
    headers.set("Authorization", `Bearer ${tokenResponse.accessToken}`);
  }
  return headers;
}
